import { Ionicons } from '@expo/vector-icons';
import { StyleSheet, Text, TextInput, View } from 'react-native';

import { Colors } from '@/constants/theme';
import { Spacing, BorderRadius } from '@/constants/spacing';
import { getBodyFont } from '@/constants/typography';
import { useColorScheme } from '@/hooks/use-color-scheme';
import type { RouteStopCandidate } from '@/lib/route-plan/RouteStopCandidateBuilder';

type LocationsStepProps = {
  homeAddress: string;
  onHomeAddressChange: (address: string) => void;
  selectedCandidates: RouteStopCandidate[];
  onLocationChange: (candidateId: string, location: string) => void;
};

/**
 * LocationsStep — wizard step 2: confirm where each stop is.
 * Home at the top (route start/end, saved to profile), one location field
 * per selected stop. Stops may be left without a location.
 */
export function LocationsStep({
  homeAddress,
  onHomeAddressChange,
  selectedCandidates,
  onLocationChange,
}: LocationsStepProps) {
  const colorScheme = useColorScheme();
  const palette = Colors[colorScheme ?? 'light'];

  return (
    <View style={styles.container}>
      {/* Home — start and end of the route */}
      <View style={[styles.card, { backgroundColor: palette.cardBackground }]}>
        <View style={styles.cardHeader}>
          <Ionicons name="home" size={18} color={palette.primary} />
          <Text style={[styles.cardTitle, { color: palette.text, fontFamily: getBodyFont() }]}>
            Home
          </Text>
          <Text style={[styles.cardHint, { color: palette.textMuted, fontFamily: getBodyFont() }]}>
            start and end
          </Text>
        </View>
        <TextInput
          style={[
            styles.input,
            { color: palette.text, borderColor: palette.border, fontFamily: getBodyFont() },
          ]}
          value={homeAddress}
          onChangeText={onHomeAddressChange}
          placeholder="Enter your home address"
          placeholderTextColor={palette.textMuted}
          maxLength={300}
        />
      </View>

      {selectedCandidates.map((candidate) => (
        <View key={candidate.id} style={[styles.card, { backgroundColor: palette.cardBackground }]}>
          <View style={styles.cardHeader}>
            <Text style={[styles.cardTitle, { color: palette.text, fontFamily: getBodyFont() }]}>
              {candidate.fixedStart ? `${candidate.fixedStart}  ` : ''}
              {candidate.title}
            </Text>
          </View>
          <TextInput
            style={[
              styles.input,
              { color: palette.text, borderColor: palette.border, fontFamily: getBodyFont() },
            ]}
            value={candidate.location}
            onChangeText={(location) => onLocationChange(candidate.id, location)}
            placeholder="Add a location (optional)"
            placeholderTextColor={palette.textMuted}
            maxLength={300}
          />
        </View>
      ))}
    </View>
  );
}

const styles = StyleSheet.create({
  container: {
    gap: Spacing.md,
  },
  card: {
    borderRadius: BorderRadius.md,
    padding: Spacing.md,
    gap: Spacing.sm,
  },
  cardHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: Spacing.sm,
  },
  cardTitle: {
    fontSize: 15,
    fontWeight: '600',
    flexShrink: 1,
  },
  cardHint: {
    fontSize: 12,
    marginLeft: 'auto',
  },
  input: {
    borderWidth: 1,
    borderRadius: BorderRadius.sm,
    paddingHorizontal: Spacing.md,
    paddingVertical: Spacing.sm,
    fontSize: 14,
  },
});
